import { useCallback, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ShadcnScope } from '@/custolink-newdesign/components/ShadcnScope'
import { Spinner } from '@/custolink-newdesign/ui/spinner'
import { AdmSupportVideoCallSessionApi } from '@/custolink-transpiled_by_server/bc.asset_interaction.api_methods'
import { elapsedAnchorFromAgo } from './roomTime'
import { RemoteSupportRoom } from './RemoteSupportRoom'
import { SessionNotesPanel } from './SessionNotesPanel'
import type { SessionNotesFlush } from './SessionNotesPanel'
import { KeyMomentsPanel } from './KeyMomentsPanel'
import { EndOfCallPromptDialog } from './EndOfCallPromptDialog'

// How long closing the window waits for the session notes to finish saving. Past this the window
// closes anyway, and the notes panel's beforeunload guard asks the worker first if still unsaved.
const NOTES_FLUSH_TIMEOUT_MS = 5000

/**
 * Route page for the worker remote-support room (`/admin/video-room`, `/video-room`), reached
 * from every existing join path (IncomingCalls, reserveWorkerVideoRoom's popup) unless the new
 * design is switched off with `?new_design=0` - the preference is inherited by the popup because
 * it is same-origin and stored in localStorage (D-01).
 *
 * Fetches room_info for the D-02 header identity and polls it while the call is not yet
 * activated (waiting for the other side) then more slowly once it is. A failed room_info fetch
 * never blocks the room itself - the header just falls back to inviteLabel, since it is purely
 * informational.
 */
export default function RemoteSupportRoomPage() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const serverUrl = params.get('serverUrl') ?? ''
  const orgId = params.get('orgId')
  const callId = params.get('callId')
  const inviteLabel = params.get('inviteLabel') ?? undefined
  // Set by the opener from the server's answer, not decided here. Absent means no notice, so a
  // link built without it never claims a call is recorded. This is only what the call was created
  // with; once room_info has loaded, its live recordingActive drives the notice instead.
  const recordingRequested = params.get('recording') === '1'
  // The window is opened during the click, before the call exists, so the popup blocker cannot
  // swallow it. See reserveWorkerVideoRoom.
  const preparing = params.get('preparing') != null

  const roomInfo = useQuery({
    queryKey: ['AdmSupportVideoCallSessionApi', AdmSupportVideoCallSessionApi.getRoomInfo.name, orgId, callId],
    queryFn: () => AdmSupportVideoCallSessionApi.getRoomInfo(Number(orgId), Number(callId)),
    enabled: orgId != null && callId != null,
    // Fast while waiting for the other side, and while recording was requested but no segment is
    // running yet (segments start once both sides are present), so the recording notice follows a
    // starting segment within seconds rather than on the next slow poll.
    refetchInterval: (query) => {
      const data = query.state.data
      if (data?.activatedAgoMs == null) return 5000
      if (recordingRequested && !data.recordingActive) return 5000
      return 30000
    },
    refetchOnWindowFocus: false,
  })

  const identity = useMemo(() => {
    const info = roomInfo.data
    const subtitle = [
      info?.assetProductName ?? '',
      info?.serialNumber ? `S/N ${info.serialNumber}` : '',
      info?.assetViewLabel ?? '',
    ]
      .filter((v) => v !== '')
      .join(' - ')

    return {
      title: info?.customerDisplayName || inviteLabel || 'Video session',
      subtitle: subtitle !== '' ? subtitle : undefined,
      details: info?.sessionTitle ? [info.sessionTitle] : [],
      elapsedAnchorMs:
        info?.activatedAgoMs != null
          ? elapsedAnchorFromAgo(roomInfo.dataUpdatedAt, info.activatedAgoMs)
          : null,
    }
  }, [roomInfo.data, roomInfo.dataUpdatedAt, inviteLabel])

  // Whether a segment is actually running right now, not whether one was requested: after an
  // egress failure, the dev duration cap or a max-duration stop the header must stop claiming the
  // call is recorded, and a restarted segment must show again. Until room_info has loaded the
  // opener's requested flag stands in.
  const recording = roomInfo.data?.recordingActive ?? recordingRequested

  // Set by SessionNotesPanel while (and after) it is mounted. Leave and a remote close reach
  // handleEnded without React ever unmounting the panel, so its unmount flush alone would never run
  // and the last few hundred ms of typing would be lost when the window closes.
  const flushNotesRef = useRef<SessionNotesFlush | null>(null)

  const handleEnded = useCallback(() => {
    // Tell the opener on every way out - the other side closed it, this browser ended it (by now
    // the debrief is saved or skipped) or the worker left - so its list and details refetch.
    // A refetch after a plain Leave is harmless. Never "*": this channel only ever needs to
    // reach the same origin (T-04-28).
    window.opener?.postMessage('call-ended', window.location.origin)
    const flush = flushNotesRef.current?.() ?? Promise.resolve()
    const timeout = new Promise<void>((resolve) => setTimeout(resolve, NOTES_FLUSH_TIMEOUT_MS))
    Promise.race([flush, timeout])
      .catch(() => {
        // A failed save still closes the window; the beforeunload guard asks first.
      })
      .finally(() => window.close())
  }, [])

  if (preparing && !token) {
    return (
      <ShadcnScope className="cl:relative cl:flex cl:h-dvh cl:flex-col cl:items-center cl:justify-center cl:gap-3 cl:bg-neutral-950 cl:text-white">
        <Spinner className="cl:size-6" />
        <p className="cl:text-base cl:font-semibold">Preparing your video call...</p>
        <p className="cl:text-sm cl:text-white/70">
          Sending the invite. This window will connect on its own.
        </p>
      </ShadcnScope>
    )
  }

  // The side panel is org-private (D-06): only ever passed when this is an org user's own room
  // (both orgId and callId present). The public /video-room route has no org context and never
  // gets a sidePanel, so guests never see it (T-04-44).
  const sidePanel =
    orgId != null && callId != null ? (
      <div className="cl:flex cl:flex-col cl:gap-6">
        <SessionNotesPanel
          orgId={Number(orgId)}
          callId={Number(callId)}
          flushRef={flushNotesRef}
        />
        <KeyMomentsPanel orgId={Number(orgId)} callId={Number(callId)} />
      </div>
    ) : undefined

  return (
    <RemoteSupportRoom
      token={token}
      serverUrl={serverUrl}
      orgId={orgId}
      callId={callId}
      inviteLabel={inviteLabel}
      recording={recording}
      identity={identity}
      sidePanel={sidePanel}
      sidePanelLabel="Notes"
      renderAfterEndSession={
        orgId != null && callId != null
          ? (done) => (
              <EndOfCallPromptDialog orgId={Number(orgId)} callId={Number(callId)} onDone={done} />
            )
          : undefined
      }
      onEnded={handleEnded}
    />
  )
}
